import { Pressable, type PressableStateCallbackType, type StyleProp, type ViewStyle } from 'react-native';
import { useRouter } from 'expo-router';
import { Text, XStack, YStack } from 'tamagui';
import { useBreakpoint, useHitSlop } from '@kiips/ui';
import { KiipsLogo } from './KiipsLogo';

/**
 * Marketing-page footer (see docs/landing-page-plan.md §6) — logo, links to
 * the public/indexable routes (kept in sync with `SEO_BY_PATH` in
 * `lib/seo.ts`), and a legal line. Horizontal link row on tablet+, stacked on
 * phone (ADR-0023 breakpoints via `useBreakpoint`).
 */

const PRODUCT_LINKS: { label: string; href: string }[] = [
  { label: 'Pricing', href: '/pricing' },
  { label: 'Family travel', href: '/family' },
  { label: 'Integrations', href: '/integrations' },
  { label: 'Contact us', href: '/contact-us' },
];

const YEAR = new Date().getFullYear();

// Shared with the logo Pressable and every FooterLink so a single closure is
// reused instead of one being allocated per link on every render.
function pressableOpacity({ pressed }: PressableStateCallbackType): StyleProp<ViewStyle> {
  return { opacity: pressed ? 0.7 : 1 };
}

const LOGO_HEIGHT = 22;

interface LandingFooterProps {
  isAuthenticated: boolean;
}

export function LandingFooter({ isAuthenticated }: LandingFooterProps) {
  const router = useRouter();
  const { md } = useBreakpoint();
  // Visual logo glyph is well under the 44pt floor (ADR-0026 / WCAG 2.5.5);
  // widen the tappable region rather than the glyph itself.
  const logoHitSlop = useHitSlop(LOGO_HEIGHT);

  return (
    <YStack borderTopWidth={1} borderColor="$borderMuted" marginTop={40} paddingTop={40} paddingBottom={32}>
      <YStack maxWidth={1100} width="100%" alignSelf="center" paddingHorizontal={28} gap={24}>
        <FooterRow asRow={md}>
          <Pressable
            onPress={() => router.push('/')}
            accessibilityLabel="kiips home"
            hitSlop={logoHitSlop}
            style={pressableOpacity}
          >
            <KiipsLogo height={LOGO_HEIGHT} accessibilityLabel="kiips home" />
          </Pressable>

          <XStack flexWrap="wrap" gap={20} rowGap={12}>
            {PRODUCT_LINKS.map((link) => (
              <FooterLink key={link.href} label={link.label} href={link.href} />
            ))}
            {!isAuthenticated ? (
              <>
                <FooterLink label="Sign in" href="/login" />
                <FooterLink label="Create account" href="/signup" />
              </>
            ) : null}
          </XStack>
        </FooterRow>

        <FooterRow asRow={md} gap={12}>
          <Text fontSize={12} color="$textSubdued">
            © {YEAR} kiips. All rights reserved.
          </Text>
          <XStack gap={16}>
            <FooterLink label="Privacy policy" href="/privacy" small />
            <FooterLink label="Terms of service" href="/terms" small />
          </XStack>
        </FooterRow>
      </YStack>
    </YStack>
  );
}

/** The logo/links row and the copyright/legal row both flip row↔column at
 *  the same breakpoint — shared here so the two rows can't drift apart. */
function FooterRow({
  asRow,
  gap = 20,
  children,
}: {
  asRow: boolean;
  gap?: number;
  children: React.ReactNode;
}) {
  return (
    <XStack
      flexDirection={asRow ? 'row' : 'column'}
      justifyContent="space-between"
      alignItems={asRow ? 'center' : 'flex-start'}
      gap={gap}
    >
      {children}
    </XStack>
  );
}

function FooterLink({ label, href, small = false }: { label: string; href: string; small?: boolean }) {
  const router = useRouter();
  return (
    <Pressable
      onPress={() => router.push(href)}
      accessibilityRole="link"
      accessibilityLabel={label}
      style={pressableOpacity}
    >
      <Text fontSize={small ? 12 : 13} fontWeight="600" color="$textSubdued">
        {label}
      </Text>
    </Pressable>
  );
}
